// Vue
import { CognitoUserPool, AuthenticationDetails, CognitoUser, CognitoRefreshToken } from 'amazon-cognito-identity-js';

// Const
import { constant } from '../../../public/config/Const';

// Service
import apiRequestService from '@/commonProject/service/apiRequestService';

// Store
import { useCommonStore } from '@/stores/common/commonStore';
import { useLoginStore } from '@/stores/login/loginStore';
import { useTouStore } from '@/stores/common/touStore';

// Utils
import Utils from '@/commonProject/helpers/utils.js';

//export default class CognitoRepository {
class CognitoRepository {
    _poolData = {};
    _userPool = null;
    _timeout = 0;
    _refreshToken = '';
    _cognitoUser = null;
    _sessionUserAttributes = null; // 初回ログイン時に初期パスワードを変更の場合の、ログイン情報を保持する変数。

    updateTimeForAccessTokenMaxLimit = 1800000; // アクセストークンの更新判定時間の上限時間 30分
    updateTimeForAccessTokenMinLimit = 1200000; // アクセストークンの更新判定時間の下限時間 20分

    constructor() {}

    /**
     * Cognitoユーザープールを生成する。
     * @returns
     */
    async setUserPool() {
        const commonStore = useCommonStore();

        let clientId = null;
        // SSOでログイン
        if (commonStore.isSsoLoggedin) {
            clientId = constant.CLIENT_ID_2; // アプリクライアント ID
        } else {
            clientId = constant.CLIENT_ID_1; // アプリクライアント ID
        }

        const poolData = {
            UserPoolId: constant.USER_POOL_ID, // ユーザープールID
            ClientId: clientId,
        };
        return new CognitoUserPool(poolData);
    }

    /**
     * CognitoのクライアントIDをコンフィグファイルから取得する。
     * 通常ログイン、もしくは、SSOログインしているなど、ログイン方法に応じて、
     * 使用するクライアントIDが異なる。
     * @returns
     */
    async setClientId() {
        const commonStore = useCommonStore();

        // SSOでログイン
        if (commonStore.isSsoLoggedin) {
            return constant.CLIENT_ID_2; // アプリクライアント ID
        } else {
            return constant.CLIENT_ID_1; // アプリクライアント ID
        }
    }

    /**
     * リダイレクト先のURLを判定する。
     * 通常ログインであれば自分自身のドメインのログイン画面に戻す。
     * SSOログインを行っている場合は、SSOログインで指定された画面にリダイレクトさせる。
     * SSOログインの戻り先は、ベンダー取得APIで事前に取得しておいたURLを使用する。
     * @returns
     */
    async setLogoutRedirectUrl() {
        const commonStore = useCommonStore();
        const loginStore = useLoginStore();
        const touStore = useTouStore();

        // ベンダーIDを一時的に記憶する。
        const VendorId = loginStore.getVendorId;

        // 現在のアクセスしているURLのドメイン部分までを取得する。
        const calledUrl = location.protocol + '//' + location.host;

        // SSOでログインしているかをフラグで判定する。
        if (commonStore.isSsoLoggedin) {
            // SSOでログインしている場合。
            // ログアウト後にタフネス画面に遷移するか判定する。
            if (!Utils.isNullEmpty(touStore.touLoginUrl)) {
                // タフネス画面に遷移する。
                return touStore.touLoginUrl;
            } else {
                // ログアウト後は、本システムのログイン画面に遷移する。
                if (Utils.isNullEmpty(VendorId)) {
                    return calledUrl + '/login';
                } else {
                    return calledUrl + '/login?vid=' + VendorId;
                }
            }
        } else {
            // 通常ログインの場合。
            // ログアウト後は、本システムのログイン画面に遷移する。
            if (Utils.isNullEmpty(VendorId)) {
                return calledUrl + '/login';
            } else {
                return calledUrl + '/login?vid=' + VendorId;
            }
        }
    }

    /**
     * SSOログインを実行する。
     * @param {*} code
     * @param {*} redirectUri
     * @returns
     */
    async token_call(code, redirectUri) {
        const params = {
            grant_type: 'authorization_code',
            client_id: constant.CLIENT_ID_2,
            redirect_uri: redirectUri,
            code: code,
        };

        const basic = 'Basic ' + btoa(constant.CLIENT_ID_2 + ':' + constant.COGNITO_KEY);
        const url = 'https://' + constant.COGNITO_AUTH_URL + '/oauth2/token';

        try {
            const response = await apiRequestService.requestServerPostForSso(url, params, basic);

            return {
                success: true,
                responseBody: response.data,
            };
        } catch (e) {
            return {
                success: false,
                responseBody: e,
            };
        }
    }

    /**
     * cognitoログイン
     * @param {*} req
     * @returns result
     */
    async cognitoLoginApi(Username, Password) {
        const authenticationData = {
            Username: Username,
            Password: Password,
        };

        const authenticationDetails = new AuthenticationDetails(authenticationData);

        const userData = {
            Username: Username,
            Pool: await this.setUserPool(),
        };

        this._cognitoUser = new CognitoUser(userData);
        this._cognitoUser.setAuthenticationFlowType('CUSTOM_AUTH');

        return new Promise((resolve, reject) => {
            this._cognitoUser.authenticateUser(authenticationDetails, {
                onSuccess: (session) => {
                    resolve({ status: 'success', resopnse: session });
                },
                onFailure: (err) => {
                    reject({ status: 'login-failed', response: err });
                },
                // Cognitoのステータスが、「FORCE_CHANGE_PASSWORD」の場合、初回パスワード強制resetフロー
                newPasswordRequired(userAttributes, requiredAttributes) {
                    // ログインしたCognitoユーザー情報の返却値から「email_verified」フィールドを除外する。
                    delete userAttributes.email_verified;

                    // 初回ログイン時のCognito処理に必要なCognitoユーザー情報を保管用変数に格納する。
                    this._sessionUserAttributes = userAttributes;

                    // Cognitoログイン処理の結果をログイン画面に返却する。
                    resolve({ status: 'first-login', response: { userAttributes, requiredAttributes } });
                },
                customChallenge(challengeParameters) {
                    // User authentication depends on challenge response
                    resolve({ status: 'mfa-required', response: challengeParameters });
                },
            });
        });
    }

    /**
     * MFAを用いてログインする。
     * @param {object} mfaCode ログイン情報
     * @returns ログイン結果
     */
    async loginByMfaCode(mfaCode) {
        //const cognitoUser = await this._getCognitoUser();
        const cognitoUser = this._cognitoUser;
        return new Promise((resolve, reject) => {
            cognitoUser.sendCustomChallengeAnswer(mfaCode, {
                onSuccess: (session) => {
                    resolve({ status: 'success', resopnse: session });
                },
                onFailure: (err) => {
                    reject({ status: 'login-failed', response: err });
                },
                // Cognitoのステータスが、「FORCE_CHANGE_PASSWORD」の場合、初回パスワード強制resetフロー
                newPasswordRequired(userAttributes, requiredAttributes) {
                    resolve({ status: 'first-login', response: { userAttributes, requiredAttributes } });
                },
                customChallenge(challengeParameters) {
                    // User authentication depends on challenge response
                    resolve({ status: 'mfa-required', response: challengeParameters });
                },
            });
        });
    }

    /**
     * ログイン後に、パスワードを再設定する。
     *
     * @param {string} oldPassword 現在のログインパスワード。
     * @param {string} newPassword 新パスワード。
     * @returns パスワード再設定結果
     */
    async changeCognitoPasswordByAfterLogin(oldPassword, newPassword) {
        const cognitoUser = await this._getCognitoUser();
        return new Promise((resolve, reject) => {
            // ログイン中のユーザーのCognitoセッションの正当性を確認する。
            cognitoUser.getSession(function (err) {
                // エラーの場合。
                if (err) {
                    reject({
                        status: 'NO SESSION',
                    });
                } else {
                    // セッションが正当な場合。
                    // Cognitoログイン中のパスワード変更を行う。
                    cognitoUser.changePassword(oldPassword, newPassword, function (err) {
                        if (err) {
                            // err.code、err.name、err.messageを取得する。
                            switch (err.message) {
                                case 'Password has previously been used':
                                    // パスワードの使いまわしチェックエラー。
                                    reject({
                                        status: 'no-password-reuse',
                                    });
                                    break;
                                default:
                                    // パスワード変更失敗。
                                    reject({
                                        status: 'change-password-failed',
                                    });
                                    break;
                            }
                        } else {
                            // パスワード変更成功。
                            resolve({
                                status: 'change-password-success',
                            });
                        }
                    });
                }
            });
        });
    }

    /**
     * ログアウトする
     * ログアウトは、Cognitoのサインアウトエンドポイントのリダイレクト処理機能を利用して、
     * ログイン画面にリダイレクトさせる。
     */
    async logout() {
        // Storeを読み込み。
        const loginStore = useLoginStore();

        // ベンダーIDを一時的に記憶する。
        const VendorId = loginStore.getVendorId;

        // ログインしているユーザーのクライアントIDを判定。
        const clientId = await this.setClientId();

        // リダイレクト先のログイン画面を判定。
        const redirectUri = await this.setLogoutRedirectUrl();

        // 各パラメーターを初期化する。
        this._timeout = 0;
        this._refreshToken = '';
        this._cognitoUser = null;

        // Cognitoサインアウトエンドポイントではブラウザのストレージ情報は削除されないので手動で削除する。
        // ローカルストレージとセッションストレージの全ての情報を削除する。
        localStorage.clear();
        sessionStorage.clear();

        // StoreにベンダーIDを復元する。
        loginStore.setVendorId(VendorId);

        // Cognitoサインアウトエンドポイントへアクセスし、認証後、リダイレクト指定したURLのログイン画面に自動遷移する。
        location.href = constant.COGNITO_SIGNOUT_ENDPOINT_URL + '?client_id=' + clientId + '&logout_uri=' + encodeURIComponent(redirectUri);
    }

    /**
     * トークン格納先のストレージキーを取得する
     *
     * @returns トークン格納先のストレージキー
     */
    async getTokenKey() {
        // トークンが有効切れか確認する。
        const cognitoToken = await this.checkTokenExpiratioByCallApi();

        if (Utils.isNull(cognitoToken)) {
            return null;
        } else {
            const currentUser = await this._getCognitoUser();
            const lastAuthUser = currentUser ? currentUser.getUsername() : null;
            const clientId = await this.setClientId();
            return `CognitoIdentityServiceProvider.${clientId}.${lastAuthUser}.idToken`;
        }
    }

    /**
     * トークンが期限切れかを確認する
     *
     * @returns トークン
     */
    async checkTokenExpiration() {
        try {
            return await new Promise((resolve, reject) => {
                this.getToken(resolve, reject);
            });
        } catch (e) {
            return null;
        }
    }

    /**
     * トークンが期限切れかを確認する
     *
     * @returns トークン
     */
    async checkTokenExpiratioByCallApi() {
        try {
            return await new Promise((resolve, reject) => {
                this.getTokenByCallApi(resolve, reject);
            });
        } catch (e) {
            return null;
        }
    }

    /**
     * 現在ブラウザに格納されているCognitoのセッショントークンを取得・判定する。
     * @param {*} resolve
     * @param {*} reject
     */
    async getToken(resolve, reject) {
        try {
            // ログイン中のCognitoユーザーを取得する。
            const currentUser = await this._getCognitoUser();

            // Cognitoユーザーを取得できない場合はトークン不正。
            if (currentUser === undefined || currentUser === null) {
                await reject(null);
            }

            // ユーザー情報からトークン情報を取得できるか処理を開始する。
            currentUser.getSession(async (err, currentSession) => {
                if (err) {
                    // セッション自体が不正の場合。
                    await reject(null);
                } else {
                    // Cognitoセッションが存在するが、有効期限が過ぎているかどうかを判定する。
                    if (currentSession && currentSession.isValid()) {
                        // 正常にCognitoセッションが存在する場合の処理。

                        // 有効なCognitoセッションからIDトークンを取得する。
                        const { idToken } = await this._getTokens(currentSession);

                        // 正常応答
                        await resolve(idToken);
                    } else {
                        // Cognitoのセッションが不正や、有効期間外の場合。

                        // 上記のいずれかでエラーが発生した場合は、トークン不正として返却する。
                        await reject(null);
                    }
                }
            });
        } catch (e) {
            // 上記のいずれかでエラーが発生した場合は、トークン不正として返却する。
            await reject(null);
        }
    }

    /**
     * 現在ブラウザに格納されているCognitoのセッショントークンを取得・判定する。
     * @param {*} resolve
     * @param {*} reject
     */
    async getTokenByCallApi(resolve, reject) {
        try {
            // ログイン中のCognitoユーザーを取得する。
            const currentUser = await this._getCognitoUser();

            // Cognitoユーザーを取得できない場合はトークン不正。
            if (currentUser === undefined || currentUser === null) {
                await reject(null);
            }

            // ユーザー情報からトークン情報を取得できるか処理を開始する。
            currentUser.getSession(async (err, currentSession) => {
                if (err) {
                    // セッション自体が不正の場合。
                    await reject(err);
                } else {
                    // Cognitoセッションが存在するが、有効期限が過ぎているかどうかを判定する。
                    if (currentSession && currentSession.isValid()) {
                        // 正常にCognitoセッションが存在する場合の処理。

                        // Storeを読み込む。
                        const commonStore = useCommonStore();

                        // 有効なCognitoセッションからIDトークンを取得する。
                        const { idToken } = await this._getTokens(currentSession);

                        // トークン有効期限判定処理のため、本APIの処理が実行された時間を保持する。
                        let nowTime = localStorage.getItem('nowTime');

                        // セッション更新監視Javascript制御処理を管理する。

                        // 画面初回起動時は、ブラウザのローカルストレージにトークン記録日時情報を格納していないので、
                        // ローカルストレージの「nowTime」フィールドの存在チェックで初期起動かどうかを判定する。
                        if (Utils.isNullEmpty(nowTime)) {
                            // 初期起動の場合。
                            try {
                                // 現在の操作を行った日時を取得する。
                                const newDate = new Date();

                                // 現時点の日時を格納する。
                                localStorage.setItem('nowTime', newDate.getTime());

                                // アクセストークンの更新判定時間の上限日時を計算する。
                                localStorage.setItem('maxLimitTime', newDate.getTime() + this.updateTimeForAccessTokenMaxLimit);

                                // アクセストークンの更新判定時間の下限日時を計算する。
                                localStorage.setItem('minLimitTime', newDate.getTime() + this.updateTimeForAccessTokenMinLimit);

                                // リフレッシュトークンを更新する。
                                await this._updateRefreshToken(currentSession);

                                // SSOでログインかどうか判定する。
                                if (commonStore.isSsoLoggedin) {
                                    // SSOログインの場合。

                                    // 既存のIDトークンを正常応答として返却する。
                                    await resolve(idToken);
                                } else {
                                    // 通常ログインの場合。

                                    // 新しいIDトークンを発行する。
                                    await resolve(await this._startRefreshToken());
                                }

                                return;
                            } catch (e) {
                                // 不正な処理が実行された場合はエラーとする。
                                await this.logout();
                                return;
                            }
                        } else {
                            // 初回起動では無く、すでにCognitoセッションが存在し、セッション更新タイミングを監視している場合。

                            // 現在の操作を行った日時を取得する。
                            const newDate = new Date();

                            // 現時点の操作している日時をローカルストレージから取得する。
                            const getLocalStorageNowTime = localStorage.getItem('nowTime');

                            // APIの非同期などで、先行実行したAPIの応答が、後続APIの応答より遅れた場合、すでにローカルストレージに格納されている時刻が、
                            // 後続APIの結果で打刻されている可能性がある。
                            // 先行実行したAPIの結果で再度ローカルストレージに格納すると、結果的に遅れて実行した結果の時刻が打刻される可能性を防ぐために、
                            // 現在の時刻と、ローカルストレージに記録されている時刻を比較し、現在の時刻が未来時刻であれば、ローカルストレージを更新する。
                            if (getLocalStorageNowTime < newDate.getTime()) {
                                // 本操作を行った時刻をローカルストレージに格納する。
                                localStorage.setItem('nowTime', newDate.getTime());

                                // 現時点の操作している日時をローカルストレージから取得する。
                                nowTime = localStorage.getItem('nowTime');
                            }

                            // Cognitoアクセストークンの有効時間を監視するためのCognitoリフレッシュトークンの更新メソッドを実行する下限時間。
                            // 前回APIが実行されたタイミングが、下限時間以下の場合は、Cognitoリフレッシュトークンの更新命令を実行しない。
                            const maxLimitTime = localStorage.getItem('maxLimitTime');

                            // Cognitoアクセストークンの有効時間を監視するためのCognitoリフレッシュトークンの更新メソッドを実行する上限時間。
                            // 前回APIが実行されたタイミングが、下限時間より大きいと上限時間以内の場合のみCognitoリフレッシュトークン更新処理を実行する。
                            // 前回APIが実行されたタイミングが、上限時間を超過している場合は、ログアウト処理を実行する。
                            const minLimitTime = localStorage.getItem('minLimitTime');

                            // Cognitoアクセストーンの有効期間の監視で、まだ有効期間内なのか判定する。その1
                            // 有効期間内なので、Cognitoリフレッシュトーンの更新処理は実行しない。
                            if (nowTime <= minLimitTime) {
                                // 画面操作を行ったので、現在時刻を基に、アクセストークンの更新判定時間の上限日時を更新する。
                                localStorage.setItem('maxLimitTime', newDate.getTime() + this.updateTimeForAccessTokenMaxLimit);

                                // 正常応答
                                await resolve(idToken);

                                // 以降の処理を実行しない。
                                return;
                            }

                            // Cognitoアクセストーンの有効期間の監視で、まだ有効期間内なのか判定する。その2
                            // もし、Cognitoアクセストークンの有効期間の下限時間を超過して、かつ、Cognitoリフレッシュトーン更新有効期間内かを判定する。
                            // Cognitoリフレッシュトークンを用いて、Cognitoアクセストークン、Cognito IDトークンの有効期間を延長する。
                            if (nowTime > minLimitTime && nowTime <= maxLimitTime) {
                                // リフレッシュトークンを更新する。
                                await this._updateRefreshToken(currentSession);

                                // SSOでログインかどうか判定する。
                                if (commonStore.isSsoLoggedin) {
                                    // SSOログインの場合。

                                    // 既存のIDトークンを正常応答として返却する。
                                    await resolve(idToken);
                                } else {
                                    // 通常ログインの場合。

                                    // 新しいIDトークンを発行する。
                                    await resolve(await this._startRefreshToken());
                                }

                                // 以降の処理を実行しない。
                                return;
                            }

                            // Cognitoリフレッシュトークンの更新可能時間を超過しているか判定する。
                            // Cognitoリフレッシュトークンの更新可能時間を超過しちえる場合は、ログアウト処理を実行する。
                            if (nowTime > maxLimitTime) {
                                // アクセストーンの有効期限外のためログアウト処理を実行する。
                                await this.logout();

                                // 以降の処理を実行しない。
                                return;
                            }
                        }
                    } else {
                        // Cognitoのセッションが不正や、有効期間外の場合。

                        // ログアウト処理を実行。
                        await this.logout();
                        return;
                    }
                }
            });
        } catch (e) {
            // 上記のいずれかでエラーが発生した場合は、トークン不正として返却する。
            await reject(null);
        }
    }

    /**
     * トークン情報を取得する
     *
     * @param {object} session セッション情報
     * @returns トークン情報
     */
    async _getTokens(session) {
        if (!session) throw 'NO SESSION';
        return {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
        };
    }

    /**
     * Cognitoユーザー情報を取得する
     *
     * @returns Cognitoユーザー情報
     */
    async _getCognitoUser() {
        let userName = null;
        userName = localStorage.getItem('mngUser');
        if (!userName) return null;
        const userData = {
            Username: userName,
            Pool: await this.setUserPool(),
        };
        return new CognitoUser(userData);
    }

    /**
     * トークンの更新を開始する。
     * リフレッシュトークンの有効時間内の場合のみ、Cognitoが自動応答で処理を実行する。
     * 本処理で、IDトークン、アクセストークンが更新され、新しいIDが発行される。
     * @returns トークン
     */
    async _startRefreshToken() {
        const cognitoUser = await this._getCognitoUser();
        return new Promise((resolve, reject) => {
            cognitoUser.refreshSession(this._refreshToken, async (err, newSession) => {
                if (err) {
                    reject(err);
                } else {
                    const { idToken } = await this._getTokens(newSession);
                    await this._updateRefreshToken(newSession);
                    resolve(idToken);
                }
            });
        });
    }

    /**
     * リフレッシュトークンを更新する。
     * 本処理で、IDトークン、アクセストークンが更新され、新しいIDが発行される。
     * @param {object} session セッション情報
     */
    async _updateRefreshToken(session) {
        const { refreshToken } = await this._getTokens(session);
        this._refreshToken = new CognitoRefreshToken({
            RefreshToken: refreshToken,
        });

        // 現在時刻を取得する。
        const newDate = new Date();

        // 現時点の操作している日時をローカルストレージから取得する。
        const getLocalStorageNowTime = localStorage.getItem('nowTime');

        // APIの非同期などで、先行実行したAPIの応答が、後続APIの応答より遅れた場合、すでにローカルストレージに格納されている時刻が、
        // 後続APIの結果で打刻されている可能性がある。
        // 先行実行したAPIの結果で再度ローカルストレージに格納すると、結果的に遅れて実行した結果の時刻が打刻される可能性を防ぐために、
        // 現在の時刻と、ローカルストレージに記録されている時刻を比較し、現在の時刻が未来時刻であれば、ローカルストレージを更新する。
        if (getLocalStorageNowTime < newDate.getTime()) {
            // 現時点の日時を格納する。
            localStorage.setItem('nowTime', newDate.getTime());

            // アクセストークンの更新判定時間の上限日時を計算する。
            localStorage.setItem('maxLimitTime', newDate.getTime() + this.updateTimeForAccessTokenMaxLimit);

            // アクセストークンの更新判定時間の下限日時を計算する。
            localStorage.setItem('minLimitTime', newDate.getTime() + this.updateTimeForAccessTokenMinLimit);
        }
    }
}

const cognitoRepository = new CognitoRepository();

export default cognitoRepository;
